import { useEffect, useState } from "react";
import api from "../lib/api";
import { currency } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import * as Icons from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

const COLORS = ["hsl(24 95% 46%)", "hsl(153 41% 30%)", "hsl(43 74% 55%)", "hsl(197 37% 34%)", "hsl(340 60% 55%)", "hsl(260 40% 50%)"];

const Stat = ({ icon, label, value, tone }) => {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <div className="bg-card border border-border rounded-md p-6 shadow-sm" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="label-caps">{label}</span>
        <span className={`p-2 rounded-md ${tone}`}><Icon size={18} /></span>
      </div>
      <div className="text-3xl font-head font-extrabold tracking-tight mt-3">{value}</div>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [stageNames, setStageNames] = useState({});
  const { user, setUiPref } = useAuth();
  const leadView = user?.ui_prefs?.lead_view === "merged" ? "merged" : "tabbed";

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => {
    api.get("/config/stages").then((r) => {
      setStageNames(Object.fromEntries((r.data || []).map((s) => [s.id, s.name])));
    }).catch(() => {});
  }, []);
  if (!stats) return <div className="text-muted-foreground">Loading…</div>;

  const stageData = Object.entries(stats.by_stage).map(([k, v]) => ({ name: stageNames[k] || k, value: v }));
  const sourceData = Object.entries(stats.by_source).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-head font-extrabold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your pipeline & activity</p>
        </div>
        <div className="bg-card border border-border rounded-md p-3" data-testid="dashboard-leadview-selector">
          <div className="label-caps mb-1.5">Lead page layout</div>
          <div className="flex gap-1">
            <button onClick={() => setUiPref("lead_view", "tabbed")} data-testid="dashboard-leadview-tabbed"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${leadView === "tabbed" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}><Icons.LayoutList size={14} /> Tabbed</button>
            <button onClick={() => setUiPref("lead_view", "merged")} data-testid="dashboard-leadview-merged"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${leadView === "merged" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}><Icons.Rows3 size={14} /> Single page</button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">Applies to every lead you open, and is remembered for you.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon="Users" label="Total Leads" value={stats.total_leads} tone="bg-secondary/10 text-secondary" />
        <Stat icon="PhoneOff" label="Not Contacted" value={stats.not_contacted} tone="bg-destructive/10 text-destructive" />
        <Stat icon="CheckCircle2" label="Worked" value={stats.worked} tone="bg-primary/10 text-primary" />
        <Stat icon="FileSignature" label="Signed Quotes" value={stats.signed_quotes} tone="bg-secondary/10 text-secondary" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-6 shadow-sm">
          <h3 className="font-head font-semibold mb-4">Leads by Stage</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stageData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill="hsl(24 95% 46%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-md p-6 shadow-sm">
          <h3 className="font-head font-semibold mb-4">Leads by Source</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={sourceData} dataKey="value" nameKey="name" outerRadius={90} label>
                {sourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-6 shadow-sm overflow-x-auto">
        <h3 className="font-head font-semibold mb-4">Rep Performance</h3>
        <table className="w-full text-sm min-w-[420px]">
          <thead><tr className="text-left label-caps border-b border-border">
            <th className="py-2">Name</th><th>Leads</th><th>Quotes</th><th>Signed</th></tr></thead>
          <tbody>
            {stats.rep_performance.map((r, i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="py-2 font-medium">{r.name}</td><td>{r.leads}</td><td>{r.quotes}</td><td>{r.signed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
